import { businessDetailQueryOptions, businessPhotosInfiniteQueryOptions } from '@/api/queries/businesses';
import { ImageViewer } from '@/components/display/image-viewer';
import { ScreenHeader } from '@/components/layout/screen-header';
import { useAppTheme } from '@/providers/theme-provider';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, Text, View } from 'react-native';

export function BusinessPhotosScreen() {
  const theme = useAppTheme();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const { data: business } = useQuery(businessDetailQueryOptions(id || ''));

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(businessPhotosInfiniteQueryOptions(id || ''));

  const allPhotos = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  return (
    <View className="flex-1 bg-background">
      <ScreenHeader
        title={business?.name ?? 'Photos'}
        showBackButton
      />

      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {allPhotos.map((item, index) => (
            <View key={item.id} style={{ width: '25%', aspectRatio: 1, padding: 3 }}>
              <Pressable
                style={{ flex: 1, borderRadius: 8, overflow: 'hidden' }}
                onPress={() => {
                  setViewerIndex(index);
                  setViewerVisible(true);
                }}
              >
                {failedIds.has(item.id) ? (
                  <View
                    className="bg-surface-secondary items-center justify-center"
                    style={{ width: '100%', height: '100%' }}
                  >
                    <Ionicons name="image-outline" size={28} className="text-muted-foreground" />
                  </View>
                ) : (
                  <Image
                    source={{ uri: imageUrl(item.r2Key || item.imageUrl, 'thumbnail') }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                    accessibilityLabel={`Photo ${index + 1}`}
                    onError={() => setFailedIds((prev) => new Set(prev).add(item.id))}
                  />
                )}
              </Pressable>
            </View>
          ))}
        </View>
        {hasNextPage && (
          <View className="py-4 items-center">
            {isFetchingNextPage ? (
              <ActivityIndicator color={theme.colors.primary} />
            ) : (
              <Pressable
                onPress={() => fetchNextPage()}
                className="px-6 py-3 rounded-lg bg-surface-secondary"
                accessibilityRole="button"
                accessibilityLabel="Load more photos"
              >
                <Text className="font-semibold text-foreground">
                  Load more
                </Text>
              </Pressable>
            )}
          </View>
        )}
      </ScrollView>

      <ImageViewer
        photos={allPhotos}
        initialIndex={viewerIndex}
        visible={viewerVisible}
        onClose={() => setViewerVisible(false)}
      />
    </View>
  );
}
